import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

interface UseJobProgressMonitorOptions {
  jobStatus?: {
    status: string;
    [key: string]: any;
  };
  onComplete?: () => void;
  queryKeysToInvalidate: string[];
  inProgressStatus?: string | string[];
  completedStatus?: string;
}

/**
 * Hook to monitor job progress and trigger actions on completion
 * @param options Configuration object
 * @returns void
 */
export function useJobProgressMonitor({
  jobStatus,
  onComplete,
  queryKeysToInvalidate,
  inProgressStatus = ['IN_PROGRESS', 'WAITING'],
  completedStatus = 'COMPLETED',
}: UseJobProgressMonitorOptions) {
  const queryClient = useQueryClient();
  const previousStatusRef = useRef<string | null>(null);

  useEffect(() => {
    if (jobStatus?.status) {
      const currentStatus = jobStatus.status;
      const previousStatus = previousStatusRef.current;

      // Check if status changed from in progress to completed
      const inProgressStatuses = Array.isArray(inProgressStatus)
        ? inProgressStatus
        : [inProgressStatus];
      if (
        previousStatus &&
        inProgressStatuses.includes(previousStatus) &&
        currentStatus === completedStatus
      ) {
        // Refetch the specified queries to get updated data
        queryClient.invalidateQueries({
          queryKey: queryKeysToInvalidate,
        });

        // Call the optional onComplete callback
        onComplete?.();
      }

      // Update the previous status ref
      previousStatusRef.current = currentStatus;
    }
  }, [jobStatus?.status, queryKeysToInvalidate, onComplete, inProgressStatus, completedStatus]);
}
